import { useDocumentTitle } from "@/lib/use-document-title";
import { useMemo, useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { usePartners, usePartnerMutations, type Partner } from "@/lib/data-hooks";
import { Handshake, Plus, Pencil, Trash2, Tag, CheckCircle2, X, Search } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const CATEGORIES = ["Service", "Skönhet & hälsa", "Mat & dryck", "Bil & motor", "Hem & trädgård", "Fritid", "Övrigt"];

function emptyPartner(): Partial<Partner> {
  return { name: "", category: CATEGORIES[0], discount_label: "", description: "", cta_url: "", active: true, sort_order: 0 };
}

function PartnersPage() {
  useDocumentTitle("Partners — ÅF-Portal");
  const { data: partners = [], isLoading } = usePartners();
  const { create, update, remove } = usePartnerMutations();
  const confirm = useConfirm();


  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [editing, setEditing] = useState<Partial<Partner> | null>(null);
  const [toast, setToast] = useState("");

  const show = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const filtered = useMemo(() => partners.filter(p => {
    if (statusFilter === "active" && !p.active) return false;
    if (statusFilter === "inactive" && p.active) return false;
    if (search && !`${p.name} ${p.category ?? ""} ${p.description ?? ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [partners, search, statusFilter]);

  const save = async (p: Partial<Partner>) => {
    if (p.id) { await update.mutateAsync(p as any); show("Partner uppdaterad"); }
    else { await create.mutateAsync(p); show("Partner skapad"); }
    setEditing(null);
  };
  const del = async (p: Partner) => {
    if (!(await confirm({ title: `Ta bort ${p.name}?`, destructive: true, confirmText: "Ta bort" }))) return;
    await remove.mutateAsync(p.id); show("Partner borttagen");
  };
  const togglePartner = async (p: Partner) => {
    await update.mutateAsync({ id: p.id, active: !p.active });
  };

  return (
    <StaffLayout title="Partners">
      {toast && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl bg-[var(--color-success)]/10 border border-[var(--color-success)]/40 text-sm shadow-lg">
          <CheckCircle2 className="text-[var(--color-success)]" size={18} /> {toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="inline-flex rounded-full bg-white border border-border px-4 py-2 text-sm font-display">
          <Handshake size={14} className="inline mr-1.5 -mt-0.5" /> {partners.length} partners
        </div>
        <button onClick={() => setEditing(emptyPartner())} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold">
          <Plus size={16} /> Ny partner
        </button>
      </div>

      <div className="card-soft p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Sök partner, kategori..." className="w-full rounded-lg border border-border pl-9 pr-3 py-2 text-sm focus:border-primary focus:outline-none" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="rounded-lg border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none">
          <option value="all">Alla statusar</option>
          <option value="active">Aktiva</option>
          <option value="inactive">Inaktiva</option>
        </select>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laddar…</p>}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && !isLoading && (
          <div className="col-span-full text-center text-sm text-muted-foreground py-12 card-soft">Inga partners matchar filtret.</div>
        )}
        {filtered.map(p => (
          <div key={p.id} className="card-soft p-5 flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="font-display font-bold text-base text-foreground leading-tight">{p.name}</div>
                {p.category && <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{p.category}</div>}
              </div>
              <span className={`shrink-0 text-[10px] uppercase tracking-wider font-display font-bold px-2 py-1 rounded-full ${p.active ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" : "bg-muted text-muted-foreground"}`}>
                {p.active ? "Aktiv" : "Pausad"}
              </span>
            </div>
            {p.discount_label && (
              <div className="inline-flex items-center gap-1.5 text-[var(--color-primary)] text-sm font-display font-semibold mb-2">
                <Tag size={13} /> {p.discount_label}
              </div>
            )}
            <p className="text-sm text-muted-foreground leading-relaxed flex-1">{p.description}</p>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
              <button onClick={() => setEditing(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-xs font-display hover:bg-muted transition">
                <Pencil size={12} /> Redigera
              </button>
              <button onClick={() => togglePartner(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-xs font-display hover:bg-muted transition">
                {p.active ? "Pausa" : "Aktivera"}
              </button>
              <button onClick={() => del(p)} aria-label="Ta bort" className="p-2 rounded-lg border border-border text-destructive hover:bg-destructive/5 transition">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Redigera partner" : "Ny partner"} onClose={() => setEditing(null)}>
          <PartnerForm initial={editing} onSubmit={save} onCancel={() => setEditing(null)} />
        </Modal>
      )}
    </StaffLayout>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} aria-label="Stäng" className="p-1.5 rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-display font-semibold text-foreground mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function PartnerForm({ initial, onSubmit, onCancel }: { initial: Partial<Partner>; onSubmit: (p: Partial<Partner>) => void; onCancel: () => void }) {
  const [p, setP] = useState<Partial<Partner>>(initial);
  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(p); }} className="space-y-4">
      <Field label="Partnerns namn *">
        <input required value={p.name ?? ""} onChange={e => setP({ ...p, name: e.target.value })} className="input-mini" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Kategori">
          <select value={p.category ?? CATEGORIES[0]} onChange={e => setP({ ...p, category: e.target.value })} className="input-mini">
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Förmån / rabatt">
          <input value={p.discount_label ?? ""} onChange={e => setP({ ...p, discount_label: e.target.value })} placeholder="t.ex. 20%" className="input-mini" />
        </Field>
      </div>
      <Field label="Beskrivning">
        <textarea value={p.description ?? ""} onChange={e => setP({ ...p, description: e.target.value })} rows={3} className="input-mini" />
      </Field>
      <Field label="Webbadress">
        <input type="url" value={p.cta_url ?? ""} onChange={e => setP({ ...p, cta_url: e.target.value })} placeholder="https://..." className="input-mini" />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={p.active ?? true} onChange={e => setP({ ...p, active: e.target.checked })} className="accent-[var(--color-primary)]" />
        Partnern är aktiv och synlig för medlemmar
      </label>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-full border border-border text-sm font-display hover:bg-muted transition">Avbryt</button>
        <button type="submit" className="flex-1 py-2.5 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold">Spara</button>
      </div>
    </form>
  );
}

export default PartnersPage;
